// GlobalNotification — toast container. Renders one Notification child
// per entry in `root.state.notifications`. Animation + hover-pause +
// timed dismissal are all driven from shared/methods/notification.js
// — this component is purely declarative.
//
// State shape per entry:
//   {
//     id: string         // unique, generated by openNotification
//     type?: 'success' | 'error' | 'info' | 'warning'
//     title?: string
//     message?: string
//     href?: string      // optional click-through target
//     notifType?: string // source NOTIFICATION type ('ticket', 'calendar_invite',
//                        // …) — distinct from the visual `type` above; rides in
//                        // from subscribeNotifications so the click can route
//                        // to the entity's feature modal
//     metadata?: object  // source notification metadata (ticketObjectId,
//                        // eventId, …) — same origin + purpose as notifType
//     icon?: string      // overrides the leading IconText.Icon name
//     action?: { call: string, args?: any[] } // run on click in the context
//                        // of the element that raised the toast (a module's
//                        // own functions included; methods/notification.js)
//     actionLabel?: string // a polyglot key: the toast grows an inline
//                        // action capsule (Undo, View…) and `action` runs
//                        // from THAT capsule only — a body click then just
//                        // dismisses, so a stray click never fires an Undo
//     _exiting?: boolean // flipped to true ~300ms before removal so the
//                        // child plays a fade-out before unmounting
//   }
//
// Interactions:
//   click toast    → href routing (if any) → action.call (if any) →
//                     el.call('closeNotification', s) — manual dismiss
//   click X icon   → el.call('closeNotification', s) — same
//   mouseover      → el.call('pauseNotification', s.id)  — hold timer
//   mouseout       → el.call('resumeNotification', s.id) — resume timer
//
// Exit animation: the animation props read `s._exiting`. When the timer
// (or manual close) flips this flag in state, the child re-renders with the
// exit keyframes instead of the entry ones. After ~300ms the notification is
// filtered out of the array and the child unmounts.
//
// THE LOOK (white-label redesign 2026-09-26, DESIGN_SPEC §5.8; r084): every
// toast is the neutral `dialog` capsule on the `float` elevation — a pill for
// one line, `radiusCard` when a message makes it two — with the TYPE carried
// by a small leading plate: the type's ink glyph on a wash of that ink
// (success / danger / warning / accent; the title ink when untyped). It was a
// full hue fill per type. Placement: bottom-centre on `--slot-bottom`, the
// composer-top line the PageSlot already uses, so a toast never covers the
// composer (Q9 A); outside the shell it falls back to `C1`. Motion: in on
// `fadeInUpShort` (6.5%) over `C` on `springBezier`, out on `modalOutDown`
// over `B` — well inside the removal delay (EXIT_MS, methods/notification.js).

export const GlobalNotification = {
  // FA204 / frank trap — TYPE_ICON used to be a bare module-scope const read
  // from inside the IconText.Icon `name` factory; it reads as undefined once
  // this component is frank.toJSON'd for platform delivery (workspace
  // d6ea1142, project_frank_drops_module_local_helpers). Moved into `scope`,
  // read via `el.scope.TYPE_ICON`.
  scope: {
    // Per-type default for the leading IconText.Icon when the caller doesn't
    // pass an explicit `icon`. Every name must resolve in a registry the
    // shell actually merges — brand designSystem/icons.js ('exclMark',
    // 'checkmark', 'info') or the shell app's own icons ('warning'). 'alert
    // circle' resolved NOWHERE and painted the hatched noIcon placeholder
    // (WS-CREDIT-TOAST-ICON-CONTRAST-1); resolvability is now pinned by
    // __tests__/globalNotificationErrorToast.test.js.
    TYPE_ICON: {
      success: 'checkmark circle',
      error: 'exclMark',
      warning: 'warning',
      info: 'info outline'
    }
  },
  childExtends: 'Notification',
  // Live region — the persistent container carries role=status + aria-live so
  // toasts are announced to screen readers as their text injects. 'polite' since
  // toasts are non-critical; the region must live on the stable container (not the
  // transient child) so it exists before content appears.
  role: 'status',
  ariaLive: 'polite',
  ariaAtomic: false,
  flow: 'y',
  gap: 'Y1',
  position: 'fixed',
  // Absolute top of the stack — toasts must always render above EVERY other
  // surface (modals 2000, dropdowns/mentions ~1300–2050, ResponsiveOverlay
  // 999999, the brand MainLoader 9990099, the Messenger chat list 9990000,
  // and even DashboardNavbar 9999999). One above the highest known layer so a
  // notification is never occluded by any chrome.
  zIndex: 10000000,
  // Above the shell call panel when one shows: the panel publishes its height
  // + gap as `--call-clear` (unset = 0). On phones the panel spans the width
  // on the same line, so a toast covered its Accept / Decline row.
  // (__tests__/toastClearsCallPanel.test.js)
  bottom: 'calc(var(--slot-bottom, var(--spacing-C1)) + var(--call-clear, 0%))',
  left: '50%',
  transform: 'translateX(-50%)',
  // Container itself shouldn't intercept pointer events when empty —
  // each toast re-enables pointer events on itself.
  pointerEvents: 'none',
  childrenAs: 'state',
  children: (el, s) => s.notifications || [],
  childProps: (el, s) => ({
    theme: 'dialog',
    color: 'title',
    minWidth: 'F3',
    maxWidth: 'G2',
    // No right room reserved for the close X any more: it is IN FLOW at the
    // end of the line (below), so neither the text nor the action capsule
    // can ever sit under its 44px target.
    padding: 'Z 0 Z Z',
    gap: 'Z',
    align: 'center flex-start',
    round: (el, _s) => (_s.message ? 'radiusCard' : 'radiusPill'),
    shadow: 'float',
    overflow: 'hidden',
    pointerEvents: 'auto',
    cursor: (el, _s) => (_s.href ? 'pointer' : 'default'),

    // Reactive motion: enter on mount, exit when `_exiting` flips — the
    // longhands, so each half carries its own duration and curve.
    animationName: (el, _s) => (_s._exiting ? 'modalOutDown' : 'fadeInUpShort'),
    animationDuration: (el, _s) => (_s._exiting ? 'B' : 'C'),
    animationTimingFunction: (el, _s) => (_s._exiting ? 'defaultBezier' : 'springBezier'),
    animationFillMode: 'both',

    onMouseover: (ev, el, _s) => {
      el.call('pauseNotification', _s.id)
    },
    onMouseout: (ev, el, _s) => {
      el.call('resumeNotification', _s.id)
    },
    onClick: (ev, el, _s) => {
      // Don't dismiss when the click bubbled up from the X icon —
      // the icon's own onClick already called closeNotification and
      // stopped propagation, so this branch only fires for body clicks.
      // Feature-modal first: a ticket/event toast opens its detail modal in
      // place; everything else keeps the shared slot-vs-navigate decision
      // (openNotificationTarget.js owns the mapping — shared with
      // NotificationRow). `notifType`/`metadata` ride in from
      // subscribeNotifications; toasts without them resolve off href alone.
      // The toast's own href, else the one its type implies (a channel message
      // or missed call → the sender's CRM contact, or the CRM inbox) — the
      // same rule as the bell row (functions/notificationHrefOf.js).
      const href = el.call('notificationHrefOf', {
        href: _s.href,
        type: _s.notifType,
        metadata: _s.metadata
      })
      if (href) {
        el.call('openNotificationTarget', { href, type: _s.notifType, metadata: _s.metadata }, el)
      }
      // action runs AFTER href routing, BEFORE dismiss — e.g. a chat-message
      // toast's { call: 'openChatFromToast', args: [channelId] } (chat's
      // subscribeRealtime.js) opens the Messenger drawer to that channel.
      // With an `actionLabel` the action belongs to its capsule, not the body.
      // The action runs in the context of the element that RAISED the toast
      // (a module's own functions included) — runNotificationAction.
      if (_s.action?.call && !_s.actionLabel) el.call('runNotificationAction', _s)
      el.call('closeNotification', { id: _s.id })
    },

    // The plate clips for its radius only (overflow law §3b.1): a long word
    // in the title or the message (a URL, an email) wraps instead of running
    // under the clip.
    Hgroup: {
      minWidth: '0',
      overflowWrap: 'anywhere',
      // fills the line, so the capsule and the X sit at its end
      flex: '1',
      H: {
        color: 'title',
        tag: 'strong',
        fontWeight: '500',
        text: (el, _s) => _s.title || ''
      },
      P: {
        color: 'caption',
        text: (el, _s) => _s.message || '',
        // Don't reserve space when there's no message line.
        hide: (el, _s) => !_s.message
      }
    },

    // THE TYPE PLATE — the Notification's own IconText, as a 20px circle:
    // the type's ink glyph on a 14% wash of that ink. Explicit `s.icon` wins
    // the glyph; the ink follows the type (untyped = the title ink).
    IconText: {
      flexShrink: '0',
      boxSize: 'iconMd',
      round: 'radiusPill',
      align: 'center center',
      color: (el, _s) =>
        ({ success: 'successInk', error: 'dangerInk', warning: 'warningInk', info: 'accentInk' })[
          _s.type
        ] || 'title',
      background: 'color-mix(in srgb, currentColor 14%, var(--color-tintBase))',
      Icon: {
        name: (el, _s) => _s.icon || el.scope.TYPE_ICON[_s.type] || 'info outline',
        boxSize: 'iconXs',
        color: 'currentColor'
      },
      Text: null
    },

    // The inline action (white-label redesign 2026-09-26: the App Store's
    // install toast routes here, Undo included) — the kit's compact ghost
    // capsule at the end of the line, before the close X.
    NotificationAction: {
      extends: 'AppCapsuleBtnGhost',
      compact: true,
      order: '1',
      hide: (el, _s) => !_s.actionLabel,
      flexShrink: '0',
      marginLeft: 'auto',
      // the 36px capsule overlaps the toast's block padding a little, so an
      // action toast keeps the one-line pill's height
      marginTop: '-Y',
      marginBottom: '-Y',
      text: (el, _s) => el.call('polyglot', _s.actionLabel || ''),
      onClick: (ev, el, _s) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (_s.action?.call) el.call('runNotificationAction', _s)
        el.parent.call('closeNotification', { id: _s.id })
      }
    },

    Icon: null,
    Button: {
      type: 'button',
      ariaLabel: (el) => el.call('polyglot', 'common.action.close'),
      // IN FLOW, last on the line (it was absolute top-right, over the
      // line's own end — the Undo capsule landed under its target). The
      // negative block margin (= the toast's own block padding) lets the
      // 44px target span the toast's height without growing a one-line
      // pill past that 44px floor.
      order: '2',
      flexShrink: '0',
      margin: '-Z 0',
      minWidth: 'controlH',
      minHeight: 'controlH',
      padding: 'Z',
      boxSizing: 'border-box',
      theme: 'transparent',
      background: 'transparent',
      color: 'caption',
      cursor: 'pointer',
      ':hover': { color: 'title' },
      Icon: { name: 'crossmark', color: 'currentColor' },
      onClick: (ev, el, _s) => {
        ev.preventDefault()
        ev.stopPropagation()
        el.parent.call('closeNotification', { id: _s.id })
      }
    }
  })
}
